import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { ApiService } from '../../services/api.service';
import type { PortfolioRepository } from '../../domain/repositories';
import type {
  Profile,
  Experience,
  Project,
  CaseStudy,
  TechnologyGroup,
  Education,
  Course,
  SiteConfig,
  SectionConfig,
  SocialLink,
  Certification,
  GitHubRepo,
  BootstrapData,
} from '../../domain/entities';

/**
 * HTTP adapter for {@link PortfolioRepository}.
 *
 * Thin layer on top of {@link ApiService}; its sole responsibility is to
 * translate domain queries into REST calls. No caching, no memoisation,
 * no signal wrapping — that belongs in use-case / view layers.
 */
@Injectable({ providedIn: 'root' })
export class HttpPortfolioRepository implements PortfolioRepository {
  private readonly api = inject(ApiService);

  public getBootstrapData(): Observable<BootstrapData> {
    return this.api.get<BootstrapData>('bootstrap');
  }

  public getProfile(): Observable<Profile> {
    return this.api.get<Profile>('profile');
  }

  public getExperiences(): Observable<Experience[]> {
    return this.api.get<Experience[]>('experience');
  }

  public getTechnologies(): Observable<TechnologyGroup[]> {
    return this.api.get<TechnologyGroup[]>('technologies');
  }

  public getFeaturedProjects(): Observable<Project[]> {
    return this.api.get<Project[]>('projects?featured=true');
  }

  public getAllProjects(): Observable<Project[]> {
    return this.api.get<Project[]>('projects');
  }

  public getProjectBySlug(slug: string): Observable<Project> {
    return this.api.get<Project>(`projects/${slug}`);
  }

  public getCaseStudy(slug: string): Observable<CaseStudy> {
    return this.api.get<CaseStudy>(`projects/${slug}/case-study`);
  }

  public getEducation(): Observable<{ education: Education[]; courses: Course[] }> {
    return this.api.get<{ education: Education[]; courses: Course[] }>('education');
  }

  public getSiteConfig(): Observable<SiteConfig> {
    return this.api.get<SiteConfig>('config/site');
  }

  public getSections(): Observable<SectionConfig[]> {
    return this.api.get<SectionConfig[]>('config/sections');
  }

  public getSocialLinks(): Observable<SocialLink[]> {
    return this.api.get<SocialLink[]>('config/social');
  }

  public getCertifications(): Observable<Certification[]> {
    return this.api.get<Certification[]>('certifications');
  }

  public getCertificationById(id: string): Observable<Certification> {
    return this.api.get<Certification>(`certifications/${id}`);
  }

  public getGithubRepos(): Observable<GitHubRepo[]> {
    return this.api.get<GitHubRepo[]>('github/repos');
  }

  public getCvUrl(): Observable<{ url: string }> {
    return this.api.get<{ url: string }>('cv/url');
  }
}
